import { useState, useRef, useEffect, useCallback, CSSProperties } from 'react';
import './TruncateTooltip.css';

interface TruncateTooltipProps {
  text: string;
  maxWidth?: string;
  className?: string;
  tooltipClassName?: string;
  tooltipStyle?: CSSProperties;
}

const TOOLTIP_GAP = 8;

export const TruncateTooltip = ({
  text,
  maxWidth = '200px',
  className = '',
  tooltipClassName = '',
  tooltipStyle = {},
}: TruncateTooltipProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text, maxWidth, checkTruncation]);

  const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!isTruncated) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({ x: rect.left, y: rect.bottom + TOOLTIP_GAP });
    setVisible(true);
  };

  const handleMouseLeave = () => setVisible(false);

  return (
    <>
      <span
        ref={textRef}
        className={`truncate-tooltip-trigger ${isTruncated ? 'is-truncated' : ''} ${className}`}
        style={{ maxWidth }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {text}
      </span>

      {visible && (
        <span
          className={`truncate-tooltip-bubble ${tooltipClassName}`}
          style={{ left: position.x, top: position.y, ...tooltipStyle }}
        >
          {text}
        </span>
      )}
    </>
  );
};

export default TruncateTooltip;
